'use client'

import { useCatalog } from '@/features/veluvit/hooks/use-catalog'
import { ownershipOf } from '@/features/veluvit/lib/rules'
import { useSessionStore } from '@/features/veluvit/stores/session.store'

import type { Catalog } from '@/features/veluvit/types/catalog.types'
import type { OwnedCard } from '@/features/veluvit/types/api.types'
import type { Ownership } from '@/features/veluvit/lib/rules'

const EMPTY_OWNED: OwnedCard[] = []
const ownershipCache = new WeakMap<Catalog, WeakMap<OwnedCard[], Ownership>>()

/** 같은 카탈로그·소유 카드 배열을 읽는 목록의 카드마다 전체 해금표를 다시 만들지 않는다. */
function sharedOwnership(catalog: Catalog, ownedCards: OwnedCard[]): Ownership {
  let cards = ownershipCache.get(catalog)
  if (!cards) {
    cards = new WeakMap()
    ownershipCache.set(catalog, cards)
  }
  let ownership = cards.get(ownedCards)
  if (!ownership) {
    ownership = ownershipOf(catalog, { ownedCards })
    cards.set(ownedCards, ownership)
  }
  return ownership
}

/** 작품 이미지 주소. kind 는 thumb·s1·hero … 또는 이미 완성된 주소 */
export function imageOf(catalog: Catalog, id: string, kind: string): string {
  if (kind.startsWith('/')) return kind
  const map = catalog.images[id]
  return map?.[kind] || map?.thumb || ''
}

/** 카탈로그 · 작품 그림만 — 세션을 구독하지 않아 턴 · 지갑 갱신에 다시 그려지지 않는다(카드 · 메시지처럼 많이 깔리는 부품용) */
export function useCatalogMedia() {
  const catalog = useCatalog()
  return {
    catalog,
    src: (id: string, kind: string) => imageOf(catalog, id, kind),
    thumb: (id: string) => imageOf(catalog, id, 'thumb'),
  }
}

/** 화면 공용 조회 — 카탈로그 + 세션 + 소유 카드 */
export function useAppData() {
  const catalog = useCatalog()
  const data = useSessionStore((s) => s.data)
  const ownership = sharedOwnership(catalog, data?.ownedCards ?? EMPTY_OWNED)
  const wallet = data?.wallet
  return {
    catalog,
    data,
    ownership,
    signed: !!data?.user,
    gem: wallet?.balance ?? 0,
    tickets: wallet?.unlockTickets ?? 0,
    turnAcc: (wallet?.totalTurns ?? 0) % 20,
    charged: !!wallet?.charged,
    src: (id: string, kind: string) => imageOf(catalog, id, kind),
    thumb: (id: string) => imageOf(catalog, id, 'thumb'),
  }
}
